// `SELF.▮` inside a `CREATE [OR REPLACE] TYPE BODY` — the OBJECT type's own
// attributes.
//
// A member method reaches the instance it was invoked on through `SELF`, so
// `SELF.` is a dotted base whose scope is the enclosing type's attribute list
// (`amount`, `currency` for oracle coverage script.39's `acact_money_t`).
// Nothing in the resolver bound it: `SELF` is not a FROM alias, not a package
// and not a schema, so the dotted branch fell through to the schema-name arm
// and emitted nothing at all.
//
// The attributes come from the snapshot's OBJECT-type registry
// (`SchemaSnapshot.objectTypeAttributes`, projected from `ALL_TYPE_ATTRS` —
// the same source the `<alias>.<objcol>.▮` attribute path already uses), keyed
// by the type name the enclosing `TYPE BODY` header names. A type the schema
// cache has not seen yet (a spec created in this very buffer but not yet
// executed) resolves to nothing rather than to a guess.
//
// Oracle-only: `SELF` is a PL/SQL object-type construct with no counterpart in
// the other four dialects.

/**
 * Name (upper-cased, matching the registry's key form) of the OBJECT type
 * whose `TYPE` / `TYPE BODY` header encloses `offset`, or null when the caret
 * is not inside one.
 *
 * Scans BACKWARD for the nearest `CREATE [OR REPLACE] TYPE [BODY] <name>`
 * header before the caret. Backward-nearest is what "enclosing" means for a
 * buffer of consecutive DDL statements: a later `CREATE TYPE BODY` opens a new
 * scope that ends the previous one.
 */
export function findOracleEnclosingObjectTypeName(
  text: string,
  offset: number,
): string | null {
  const head = text.slice(0, Math.max(0, offset));
  const re =
    /\bCREATE\s+(?:OR\s+REPLACE\s+)?(?:EDITIONABLE\s+|NONEDITIONABLE\s+)?TYPE\s+(?:BODY\s+)?("(?:[^"]|"")*"|[A-Za-z_][\w$]*)/gi;
  let match: RegExpExecArray | null;
  let last: string | null = null;
  while ((match = re.exec(head)) !== null) {
    const raw = match[1]!;
    last = raw.startsWith('"')
      ? raw.slice(1, -1).replace(/""/g, '"')
      : raw.toUpperCase();
  }
  return last;
}
